import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../services/api';

interface UtilisationPoint {
  at: string;
  average: number;
  maximum: number;
}

/** Percent of the service's reserved CPU or memory; null when CloudWatch has no points. */
interface Utilisation {
  current: number | null;
  peak: number | null;
  points: UtilisationPoint[];
}

/** GET /api/sysdevops/system/metrics */
interface SystemMetrics {
  cluster: string;
  service: string;
  environment: string;
  windowMinutes: number;
  cpu: Utilisation;
  memory: Utilisation;
  tasks: { running: number | null; desired: number | null; pending: number | null };
  rolloutState: string | null;
  deployedCommit: string | null;
}

interface LogLine {
  at: string;
  task: string;
  message: string;
}

/**
 * A key per line, made once when the lines arrive. Two lines can share a
 * task and a millisecond, so the key counts repeats of that pair.
 */
const withKeys = (lines: LogLine[]) => {
  const seen = new Map<string, number>();
  return lines.map((line) => {
    const base = `${line.at}|${line.task}`;
    const n = (seen.get(base) ?? 0) + 1;
    seen.set(base, n);
    return { ...line, key: `${base}|${n}` };
  });
};

// CloudWatch publishes ECS utilisation every five minutes, so asking more
// often than this only shows the same numbers again.
const REFRESH_MS = 60_000;
const LOG_LIMITS = [50, 100, 200];

const percent = (value: number | null) => (value === null ? '–' : `${value.toFixed(1)}%`);

const usageColor = (value: number | null) => {
  if (value === null) return 'text-gray-400';
  if (value >= 85) return 'text-brand-red';
  if (value >= 60) return 'text-brand-blue-light';
  return 'text-scarf-green';
};

const barColor = (value: number) => {
  if (value >= 85) return 'bg-brand-red';
  if (value >= 60) return 'bg-brand-blue-light';
  return 'bg-scarf-green';
};

const UtilisationCard: React.FC<{ title: string; usage: Utilisation; windowMinutes: number }> = ({ title, usage, windowMinutes }) => (
  <section aria-label={title} className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
    <h3 className="text-lg font-title font-bold text-deep-navy mb-2">{title}</h3>
    <div className={`text-3xl font-bold ${usageColor(usage.current)}`} data-testid="current">
      {percent(usage.current)}
    </div>
    <p className="text-gray-500 text-sm mb-4">
      {usage.current === null
        ? `No data in the last ${windowMinutes} minutes`
        : `Peak ${percent(usage.peak)} in the last ${windowMinutes} minutes`}
    </p>
    {usage.points.length > 0 && (
      <div className="flex items-end gap-1 h-16" aria-hidden="true">
        {usage.points.map((point) => (
          <div
            key={point.at}
            title={`${new Date(point.at).toLocaleTimeString()}: ${point.average.toFixed(1)}%`}
            className={`flex-1 rounded-t ${barColor(point.average)}`}
            style={{ height: `${Math.max(2, Math.min(100, point.average))}%` }}
          />
        ))}
      </div>
    )}
  </section>
);

/**
 * The backend's own resource use and logs, from ECS and CloudWatch.
 *
 * This tab used to invent everything every five seconds: CPU temperature,
 * disk, network, load average, and a process list. It now shows the
 * backend service's CPU and memory utilisation, its tasks and deployed
 * commit, and the latest lines its containers logged.
 */
const SystemComponent: React.FC = () => {
  const [metrics, setMetrics] = useState<SystemMetrics | null>(null);
  const [metricsError, setMetricsError] = useState<string | null>(null);
  const [lines, setLines] = useState<(LogLine & { key: string })[]>([]);
  const [logGroup, setLogGroup] = useState<string | null>(null);
  const [logsError, setLogsError] = useState<string | null>(null);
  const [logLimit, setLogLimit] = useState(LOG_LIMITS[0]);
  const [isLoading, setIsLoading] = useState(false);

  // Metrics and logs are fetched independently: one being unavailable should
  // not hide the other.
  const fetchSystemData = useCallback(async () => {
    setIsLoading(true);
    const [metricsResult, logsResult] = await Promise.allSettled([
      apiService.getSystemMetrics(),
      apiService.getSystemLogs(logLimit),
    ]);

    if (metricsResult.status === 'fulfilled') {
      setMetrics((metricsResult.value.data as SystemMetrics | undefined) ?? null);
      setMetricsError(null);
    } else {
      setMetrics(null);
      setMetricsError(metricsResult.reason instanceof Error ? metricsResult.reason.message : 'Could not load system metrics');
    }

    if (logsResult.status === 'fulfilled') {
      const data = logsResult.value.data as { logGroup?: string; lines?: LogLine[] } | undefined;
      setLines(withKeys(data?.lines ?? []));
      setLogGroup(data?.logGroup ?? null);
      setLogsError(null);
    } else {
      setLines([]);
      setLogsError(logsResult.reason instanceof Error ? logsResult.reason.message : 'Could not load logs');
    }
    setIsLoading(false);
  }, [logLimit]);

  useEffect(() => {
    fetchSystemData();
    const interval = setInterval(fetchSystemData, REFRESH_MS);
    return () => clearInterval(interval);
  }, [fetchSystemData]);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm flex flex-wrap justify-between items-center gap-4">
        <div>
          <h3 className="text-xl font-title font-bold text-deep-navy">Backend Service</h3>
          {metrics && (
            <p className="text-gray-500 text-sm break-all">
              {metrics.service} · {metrics.environment}
            </p>
          )}
        </div>
        <button
          onClick={fetchSystemData}
          disabled={isLoading}
          className="bg-brand-blue-light hover:bg-brand-blue disabled:bg-brand-blue-light/50 text-white font-bold px-6 py-2 rounded-xl transition-colors"
        >
          {isLoading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {metricsError && (
        <div role="alert" className="bg-red-50 border border-red-200 text-brand-red rounded-xl p-4">
          {metricsError}
        </div>
      )}

      {metrics && (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <UtilisationCard title="CPU" usage={metrics.cpu} windowMinutes={metrics.windowMinutes} />
          <UtilisationCard title="Memory" usage={metrics.memory} windowMinutes={metrics.windowMinutes} />
          <section aria-label="Tasks" className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Tasks</h3>
            <div className="text-3xl font-bold text-deep-navy" data-testid="running">
              {metrics.tasks.running ?? '–'} / {metrics.tasks.desired ?? '–'}
            </div>
            <p className="text-gray-500 text-sm">
              Running of desired{metrics.tasks.pending ? ` · ${metrics.tasks.pending} starting` : ''}
            </p>
          </section>
          <section aria-label="Deployment" className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Deployment</h3>
            <div className="text-lg font-bold font-mono text-deep-navy">{metrics.deployedCommit ?? '–'}</div>
            <p className="text-gray-500 text-sm">{metrics.rolloutState ? `Rollout ${metrics.rolloutState.toLowerCase()}` : 'No rollout state'}</p>
          </section>
        </div>
      )}

      <section aria-label="Logs" className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
          <div>
            <h3 className="text-xl font-title font-bold text-deep-navy">Recent Logs</h3>
            {logGroup && <p className="text-gray-500 text-sm font-mono break-all">{logGroup}</p>}
          </div>
          <select
            value={logLimit}
            onChange={(e) => setLogLimit(Number(e.target.value))}
            aria-label="Lines to show"
            className="bg-frost-white border border-ice-teal-light rounded-lg px-4 py-2 text-gray-800 focus:outline-none focus:border-brand-blue"
          >
            {LOG_LIMITS.map((limit) => (
              <option key={limit} value={limit}>Latest {limit}</option>
            ))}
          </select>
        </div>

        {logsError && (
          <div role="alert" className="bg-red-50 border border-red-200 text-brand-red rounded-xl p-4">
            {logsError}
          </div>
        )}

        {!logsError && lines.length === 0 && !isLoading && (
          <p className="text-gray-500">No log lines yet.</p>
        )}

        {lines.length > 0 && (
          <ol className="bg-deep-navy rounded-xl p-4 max-h-96 overflow-y-auto font-mono text-xs space-y-1">
            {lines.map((line) => (
              <li key={line.key} className="text-frost-white whitespace-pre-wrap break-all">
                <span className="text-ice-teal-light">{new Date(line.at).toLocaleTimeString()}</span>{' '}
                <span className="text-gray-400">{line.task.slice(0, 8)}</span>{' '}
                {line.message}
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
};

export default SystemComponent;
